import { ToggleMenuEvent } from "@iunctim/component-navigation/Resources/Public/Scripts/events/ToggleMenuEvent.js";
import { ScrollLock } from "@iunctim/component-overlay/Resources/Public/Scripts/features/ScrollLock.js";
import { Popup } from "../abstractions/Popup.js";

/**
 * Collapsible behaviour for the
 * composition within responsive menus
 */
export class Collapsible extends Popup {
    private menu: HTMLElement;

    /**
     * Constructor of the Collapsible
     *
     * @param {HTMLElement} scope
     */
    public constructor(scope: HTMLElement) {
        super();

        this.menu = document.querySelector<HTMLElement>(
            Object.entries({
                scope: "header-main-menu-behaviour",
                template: "collapsible",
                context: scope.getAttribute("context"),
            })
                .filter(([_, value]) => !!value)
                .reduce((s, [key, value]) => {
                    return `${s}[${key}="${value}"]`;
                }, ""),
        )!;

        document.body.appendChild(this.menu);

        this.levels = this.createLevels(this.menu);

        this.menu.setAttribute(
            "has-submenus",
            this.levels.length ? "true" : "false",
        );
    }

    /**
     * Method to apply the functionality
     *
     * @returns {void}
     */
    public apply(): void {
        // Listen to state updates
        document.addEventListener(ToggleMenuEvent.KEY, this.handleStateUpdates);

        // Listen to escape-key events
        document.addEventListener("keydown", this.handleEscapeKey);

        // Listen to link-click events;
        // especially needed for anchor-links
        this.menu.addEventListener("click", this.handleLinkClick);

        this.levels.forEach((level) => level.apply());
    }

    /**
     * Method to destroy the functionality
     *
     * @returns {void}
     */
    public destroy(): void {
        // Stop listening to escape-key events
        document.removeEventListener("keydown", this.handleEscapeKey);

        // Stop listening to link-click events
        this.menu.removeEventListener("click", this.handleLinkClick);

        // Close the menu in case it is open
        document.dispatchEvent(
            new ToggleMenuEvent({
                detail: {
                    action: "close",
                    identifier: this.menu.id,
                },
            }),
        );

        // Stop listening to state updates
        document.removeEventListener(
            ToggleMenuEvent.KEY,
            this.handleStateUpdates,
        );

        this.levels.forEach((level) => level.destroy());
    }

    /**
     * Method to open the menu
     *
     * @returns {void}
     */
    protected open(): void {
        if (this.menu.hidden) {
            // Show the menu
            this.menu.hidden = false;

            // Enable the scroll-lock
            ScrollLock.use().lock();
        }
    }

    /**
     * Method to close the menu
     *
     * @returns {void}
     */
    protected close(): void {
        if (!this.menu.hidden) {
            // Hide the menu
            this.menu.hidden = true;

            // Disable the scroll-lock
            ScrollLock.use().unlock();
        }
    }

    /**
     * Handles updates to the state of the menu
     *
     * @returns {void}
     */
    protected handleStateUpdates = (event: ToggleMenuEvent): void => {
        if (event.detail.identifier === this.menu.id) {
            switch (event.detail.action) {
                case "open":
                    this.open();
                    break;

                case "close":
                    this.close();
                    break;
            }
        }
    };

    /**
     * Handles the closing via escape-key
     *
     * @returns {void}
     */
    protected handleEscapeKey = (event: KeyboardEvent): void => {
        if (event.key === "Escape") {
            document.dispatchEvent(
                new ToggleMenuEvent({
                    detail: {
                        action: "close",
                        identifier: this.menu.id,
                    },
                }),
            );
        }
    };

    /**
     * Handles closing the menu when a link is clicked
     *
     * @param {MouseEvent} event
     * @returns {void}
     */
    private handleLinkClick = (event: MouseEvent): void => {
        const target = event.target;

        if (!(target instanceof Element)) {
            return;
        }

        const link = target.closest("a[href]");

        if (!link || !this.menu.contains(link)) {
            return;
        }

        document.dispatchEvent(
            new ToggleMenuEvent({
                detail: {
                    action: "close",
                    identifier: this.menu.id,
                },
            }),
        );
    };
}
